import { useState } from "react";
import { Menu, X } from "lucide-react";
import { AnimatedHeading, FadeIn } from "@/components/Motion";
import { useAuth } from "@/lib/auth";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden flex flex-col text-white"
    >
      {/* Raw video background — no overlay */}
      <video
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
        data-testid="hero-video"
      >
        <source
          src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260403_050628_c4e32401-fab4-4a27-b7a8-6e9291cd5959.mp4"
          type="video/mp4"
        />
      </video>

      {/* Navbar — fixed so it stays available while scrolling */}
      <div className="fixed top-0 left-0 right-0 z-50 px-6 md:px-12 lg:px-16 pt-6">
        <NavBar />
      </div>

      {/* Hero content pinned to bottom */}
      <div className="relative z-10 flex-1 flex flex-col justify-end px-6 md:px-12 lg:px-16 pb-12 lg:pb-16">
        <div className="lg:grid lg:grid-cols-2 lg:items-end lg:gap-10">
          {/* Left */}
          <div>
            <AnimatedHeading
              text={"Shaping tomorrow\nwith vision and action."}
              className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-normal mb-4 text-white"
              style={{ letterSpacing: "-0.04em" }}
              initialDelay={200}
              charDelay={30}
              charDuration={500}
            />
            <FadeIn delay={800} duration={1000}>
              <p className="text-base md:text-lg text-gray-300 mb-5 max-w-xl">
                We design and build premium websites, web apps and AI products that define what comes next.
              </p>
            </FadeIn>
            <FadeIn delay={1200} duration={1000}>
              <div className="flex flex-wrap gap-4">
                <a
                  href="mailto:novacreationscodes@gmail.com?subject=New%20Project%20Inquiry%20—%20Nova%20Creations%20Codes&body=Hi%2C%0A%0AI'd%20like%20to%20start%20a%20project%20with%20Nova%20Creations%20Codes.%0A%0AName%3A%0ACompany%3A%0APhone%3A%0AService%20interest%3A%0ABudget%20(USD)%3A%0AProject%20description%3A%0A%0AThanks!"
                  data-testid="hero-cta-primary"
                  className="bg-white text-black px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                >
                  Start a Chat
                </a>
                <a
                  href="#services"
                  data-testid="hero-cta-secondary"
                  className="liquid-glass border border-white/20 text-white px-8 py-3 rounded-lg font-medium hover:bg-white hover:text-black transition-colors"
                >
                  Explore Now
                </a>
              </div>
            </FadeIn>
          </div>

          {/* Right tag card */}
          <div className="mt-12 lg:mt-0 flex items-end justify-start lg:justify-end">
            <FadeIn delay={1400} duration={1000}>
              <div
                className="liquid-glass border border-white/20 px-6 py-3 rounded-xl"
                data-testid="hero-tag"
              >
                <span className="text-lg md:text-xl lg:text-2xl font-light text-white">
                  Design. Development. AI.
                </span>
              </div>
            </FadeIn>
          </div>
        </div>
      </div>
    </section>
  );
}

function NavBar() {
  const { user, login, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const links = [
    { href: "#services", label: "Services" },
    { href: "#process", label: "Process" },
    { href: "#pricing", label: "Pricing" },
    { href: "#about", label: "About" },
    { href: "#contact", label: "Contact" },
  ];
  return (
    <nav
      className="liquid-glass rounded-xl px-4 py-2 flex items-center justify-between relative"
      data-testid="site-nav"
    >
      <a href="#top" data-testid="nav-logo" className="text-2xl font-semibold tracking-tight text-white">
        NOVA
      </a>
      <div className="hidden md:flex items-center gap-8">
        {links.map((l) => (
          <a
            key={l.href}
            href={l.href}
            data-testid={`nav-link-${l.label.toLowerCase()}`}
            className="text-sm text-white hover:text-gray-300 transition-colors"
          >
            {l.label}
          </a>
        ))}
      </div>
      {menuOpen && (
        <div
          data-testid="mobile-menu"
          className="absolute top-full left-0 right-0 mt-2 md:hidden liquid-glass rounded-xl border border-white/10 p-4 flex flex-col gap-1"
        >
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
              onClick={() => setMenuOpen(false)}
              className="px-3 py-3 text-white hover:bg-white/10 rounded-lg transition-colors"
            >
              {l.label}
            </a>
          ))}
          {user?.is_admin && (
            <a href="/admin" data-testid="mobile-nav-admin" className="px-3 py-3 text-white hover:bg-white/10 rounded-lg transition-colors">
              Admin
            </a>
          )}
        </div>
      )}
      <div className="flex items-center gap-3">
        <button
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          data-testid="mobile-menu-toggle"
          onClick={() => setMenuOpen(!menuOpen)}
          className="md:hidden w-9 h-9 grid place-items-center text-white"
        >
          {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
        {user ? (
          <>
            {user.is_admin && (
              <a href="/admin" data-testid="nav-admin" className="hidden sm:inline text-sm text-white hover:text-gray-300">
                Admin
              </a>
            )}
            {user.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />}
            <button data-testid="nav-logout" onClick={logout} className="text-sm text-white hover:text-gray-300">
              Logout
            </button>
          </>
        ) : (
          <button
            onClick={login}
            data-testid="nav-google-signin"
            className="bg-white text-black px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors flex items-center gap-2"
          >
            <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
            Sign in
          </button>
        )}
      </div>
    </nav>
  );
}
